import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';
import {
  Button,
  DeleteModal,
  ExpandableTable,
  ICONS,
  Loader,
  Modal,
  PLATFORM_THEME_COLORS,
  Tabs,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetInterviewTypes } from 'src/hooks/interview-types/get-interview-types.hook';
import { useDeleteInterviewType } from 'src/hooks/interview-types/delete-interview-type.hook';
import { useCreateInterviewType } from 'src/hooks/interview-types/create-interview-type.hook';
import PermissionCheck from 'src/components/permission-check';

import { InterviewTypeForm } from './components/interview-type-form';
import { InterviewTypeCard } from './components/interview-type-card';
import { MainContainer, TabContentWrapper } from './employee-development.styles';
import {
  DEFAULT_INTERVIEW_TYPE_TO_DELETE,
  EMPLOYEE_DEVELOPMENT_ACTIONS,
} from './employee-development.constants';
import { EditInterviewType } from './components/interview-type-edit';

const EmployeeDevelopment = () => {
  const { t } = useTranslation('settings');
  const [currentAction, setCurrentAction] = useState<{ action: string; data?: any }>(
    DEFAULT_INTERVIEW_TYPE_TO_DELETE
  );

  const { interviewTypes, loading, onTableChange, paginationConfig } = useGetInterviewTypes();
  const createInterviewType = useCreateInterviewType();
  const deleteInterviewType = useDeleteInterviewType();

  const closeCurrentModal = () => {
    setCurrentAction(DEFAULT_INTERVIEW_TYPE_TO_DELETE);
  };

  const openCurrentModal = (action: string, currentData?: any) => {
    if (currentData) {
      setCurrentAction({ action, data: currentData });
    }

    setCurrentAction(oldValue => ({ ...oldValue, action }));
  };

  const onDeleteSubmit = () => {
    deleteInterviewType({
      variables: { interviewTypeId: currentAction.data._id },
      onCompleted: closeCurrentModal,
    });
  };

  const onCreateSubmit = (values: any) => {
    createInterviewType({
      variables: { payload: values },
      onCompleted: closeCurrentModal,
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <MainContainer>
      <Modal
        isOpened={currentAction?.action === EMPLOYEE_DEVELOPMENT_ACTIONS.create}
        title={t('employeeDevelopment.labels.createInterviewType')}
        onClose={closeCurrentModal}
      >
        <InterviewTypeForm onFinish={onCreateSubmit} onClose={closeCurrentModal} />
      </Modal>
      <DeleteModal
        translate={t}
        isOpened={currentAction?.action === EMPLOYEE_DEVELOPMENT_ACTIONS.delete}
        title={t('employeeDevelopment.deleteModal.title')}
        subTitle={t('employeeDevelopment.deleteModal.subtitle', { name: currentAction?.data.name })}
        onClose={closeCurrentModal}
        onSubmit={onDeleteSubmit}
      />
      <Tabs>
        <Tabs.TabPane tab={t('employeeDevelopment.labels.employeeInterview')}>
          {currentAction?.action === EMPLOYEE_DEVELOPMENT_ACTIONS.edit ? (
            <EditInterviewType onClose={closeCurrentModal} initialValues={currentAction.data} />
          ) : (
            <TabContentWrapper>
              <div>
                <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
                  {t('employeeDevelopment.labels.employeeInterview')}
                </Typography>
                <PermissionCheck
                  permission={PLATFORM_PERMISSIONS.companyManagement}
                  action={PERMISSION_ACTIONS.create}
                >
                  <Button
                    type="primary"
                    ghost
                    icon={ICONS.plus}
                    iconColor={PLATFORM_THEME_COLORS.primary.blue}
                    onClick={() => openCurrentModal(EMPLOYEE_DEVELOPMENT_ACTIONS.create)}
                  >
                    {t('employeeDevelopment.buttons.createInterviewType')}
                  </Button>
                </PermissionCheck>
              </div>
              <ExpandableTable
                dataSource={interviewTypes}
                paginationConfig={paginationConfig}
                onChange={onTableChange}
                t={t}
                expandedRowRender={record => (
                  <InterviewTypeCard data={record} handleAction={openCurrentModal} />
                )}
              >
                <ExpandableTable.Column
                  sorter
                  title={t('employeeDevelopment.labels.interviewType')}
                  key="name"
                  dataIndex="name"
                  render={name => <Typography>{name}</Typography>}
                />
              </ExpandableTable>
            </TabContentWrapper>
          )}
        </Tabs.TabPane>
      </Tabs>
    </MainContainer>
  );
};

export default EmployeeDevelopment;
